import React, { useContext, useState, useEffect, useRef } from 'react';
import { Popover } from '@headlessui/react';
import { AuthContext } from "../contexts/auth";
import Footer from "../components/footer";
import { importEmployees, downloadTemplate, fetchInstructions } from '../services/api_import_employees';
import Swal from 'sweetalert2';
import { Link } from 'wouter';

export default function UploadCSVEmployee() {
    const { profile, token } = useContext(AuthContext);
    const [selectedFile, setSelectedFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [instructions, setInstructions] = useState([]);
    const [error, setError] = useState(null);
    // const [data, setData] = useState({ headers: [], instructions: [] });
    const fileInputRef = useRef(null);

    const handleFileChange = (event) => {
        setSelectedFile(event.target.files[0]);
    };


    const handleImport = async () => {
        if (!selectedFile) {
            Swal.fire({
                icon: 'warning',
                title: 'Nessun file selezionato',
                text: 'Seleziona un file CSV prima di confermare',
            });
            return;
        }
    
        setLoading(true);
        try {
            const result = await importEmployees(token, selectedFile, profile.profile.id);
            
            let icon = 'error'; 
            if (result.message.includes("successo") && result.message.includes("errori")) {
                icon = 'warning'; 
            } else if (result.message.includes("successo")) {
                icon = 'success';
            }
    
            Swal.fire({
                icon: icon,
                title: icon === 'success' ? 'Importazione completata!' : (icon === 'warning' ? 'Importazione completata con avvisi' : 'Errore durante l\'importazione'),
                html: result.message,
                allowOutsideClick: false,
            });
            
        } catch (error) {
            const errorMessage = error.response?.data?.error || 'Si è verificato un errore durante l\'importazione.';
            const errorDetails = error.response?.data?.details || '';
            
            const displayMessage = errorDetails ? errorDetails : errorMessage;
            
            Swal.fire({
                icon: 'error',
                title: 'Errore',
                html: displayMessage, 
                allowOutsideClick: false,
            });
        } finally {
            setLoading(false);
            setSelectedFile(null);
        }        
    };
    

    useEffect(() => {
        if (!selectedFile) {
            fileInputRef.current.value = '';
        }
    }, [selectedFile]);
    

    const handleDownloadTemplate = async () => {

        try {
            await downloadTemplate(token);
            console.log('Template scaricato con successo');
        } catch (error) {
            console.error('Errore nel download del template:', error);
        }
    };

    useEffect(() => {
        const loadInstructions = async () => {
            try {
                const data = await fetchInstructions(token);

                // setData(response.data);
                setInstructions(data);
                
            } catch (err) {
                setError(err.message);
            }
        };

        loadInstructions();
    }, [token]);


    return (
        <div className="flex flex-col min-h-screen">
            <Popover as="header" className='bg-indigo-200'>
                <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:max-w-7xl lg:px-8">
                    <div className="relative flex flex-wrap items-center justify-center lg:justify-between">
                        <div className="w-full -5 lg:border-t lg:border-white lg:border-opacity-20">
                            <div className="flex justify-center my-2 py-2">
                                <h2 className="text-center text-2xl font-semibold text-indigo-800">Importa Dipendenti</h2>
                            </div>
                        </div>
                    </div>
                </div>
            </Popover>
            <main className="flex-grow mt-10 pb-8">
                <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:max-w-7xl lg:px-8">
                    <div className='justify-end flex'>
                        <button 
                            className="flex items-center justify-center text-gray-700 font-semibold px-1 border border-solid shadow-md me-3 rounded-md hover:bg-gray-100" 
                            title="Back" 
                        >
                            <Link to={`/dipendenti`} className='flex items-center'>
                                <p className='p-2 text-sm'>Torna indietro</p>
                            
                            </Link>
                        </button>
                    </div>
                    <h1 className="text-xl font-bold my-4 text-indigo-700">Carica il tuo file CSV</h1>
                    <input
                        type="file"
                        accept=".csv"
                        ref={fileInputRef}
                        className="block file:py-6 w-full border border-gray-200 rounded-md shadow-md mb-4 text-sm text-slate-500
                            file:mr-4 file:px-4
                            file:rounded-75 file:border-0
                            file:text-sm file:font-semibold
                            file:bg-indigo-50 file:text-indigo-700
                            hover:file:bg-indigo-100
                        "
                        onChange={handleFileChange}
                    />
                    <p className='text-sm text-gray-800 font-normal'>Scarica il template di esempio <span onClick={handleDownloadTemplate} className='text-indigo-800 font-semibold cursor-pointer'>QUI</span></p>
                    <button
                        onClick={handleImport}
                        className={`flex w-40 items-center justify-center gap-x-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 mx-auto`}
                        disabled={loading}
                    >
                        {loading ? 'Importando...' : 'Conferma'}
                    </button>
                    <hr className='my-6'></hr>
                    <p className='text-lg font-bold my-4 text-indigo-700'>Segui le istruzioni per la compilazione del file CSV</p>
                </div>

                <div className='mx-auto max-w-3xl px-4 sm:px-6 lg:max-w-7xl lg:px-8'>
                    <table className="min-w-full">
                        <thead className="bg-indigo-50 text-indigo-800">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider rounded-l-lg">Colonna</th>
                                <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider rounded-r-lg">Tipo</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">               
                            {instructions && Object.keys(instructions).map((fieldname, index) => {
                                const field = instructions[fieldname];
                                return <tr key={index}>
                                    <td className='p-1 pl-4 text-sm text-gray-800 font-normal'>{field.name}</td>
                                    <td className='p-1 text-sm text-gray-800 font-normal' dangerouslySetInnerHTML={{ __html: field.type || 'N/A' }} />
                                </tr>
                            })}
                        </tbody>
                    </table>
                </div>
            </main>
            <Footer />
        </div>
    );
}
